import { motion } from "framer-motion";
import { ListTodo, CalendarClock, BadgeCheck, Ruler, Image, FileText, MapPin, Users, History, LayoutDashboard, Check } from "lucide-react";
import { Reveal, Chapter, SectionHeading } from "./Reveal";

const spot = (e) => {
  const r = e.currentTarget.getBoundingClientRect();
  e.currentTarget.style.setProperty("--mx", `${e.clientX - r.left}px`);
  e.currentTarget.style.setProperty("--my", `${e.clientY - r.top}px`);
};

const Tile = ({ icon: Icon, title, sub, className = "", children, delay = 0 }) => (
  <motion.div
    onMouseMove={spot}
    initial={{ opacity: 0, y: 34 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.7, delay, ease: [0.16, 1, 0.3, 1] }}
    whileHover={{ scale: 1.02 }}
    className={`spot-card rounded-3xl bg-white border border-diem-line shadow-[0_8px_30px_rgb(0,0,0,0.04)] p-6 flex flex-col ${className}`}
    data-testid={`project-tile-${title.toLowerCase().replace(/\s+/g, "-")}`}
  >
    <div className="flex items-center gap-3 mb-2">
      <span className="w-9 h-9 rounded-xl bg-diem-blue/10 flex items-center justify-center shrink-0">
        <Icon className="w-4.5 h-4.5 w-[18px] h-[18px] text-diem-blue" />
      </span>
      <h3 className="font-display text-lg font-semibold tracking-tight text-diem-ink">{title}</h3>
    </div>
    <p className="text-sm text-diem-muted leading-relaxed">{sub}</p>
    {children}
  </motion.div>
);

export const Projects = () => (
  <section id="projects" className="bg-white py-28 sm:py-36" data-testid="projects-section">
    <div className="max-w-7xl mx-auto px-6 lg:px-8">
      <Chapter index="04" label="Projects" />
      <div className="mt-8 max-w-3xl">
        <Reveal delay={0.1}>
          <SectionHeading>Sometimes a chat becomes a project.</SectionHeading>
        </Reveal>
        <Reveal delay={0.2}>
          <p className="mt-7 text-lg leading-relaxed text-diem-slate">
            Turn a conversation into a Project and Deyem gives everything inside it somewhere to live.
          </p>
        </Reveal>
      </div>

      <div className="mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-5" data-testid="projects-bento-grid">
        <Tile icon={LayoutDashboard} title="Summary" sub="Everything important at a glance." className="lg:col-span-8 lg:row-span-2" delay={0}>
          <div className="mt-6 grid sm:grid-cols-3 gap-3">
            {[["3", "open tasks"], ["Fri", "next deadline"], ["12", "photos"]].map(([v, l]) => (
              <div key={l} className="rounded-2xl bg-diem-paper border border-diem-line p-4">
                <p className="font-display text-3xl font-semibold tracking-tight text-diem-blue">{v}</p>
                <p className="text-xs font-semibold text-diem-muted mt-1">{l}</p>
              </div>
            ))}
          </div>
          <div className="mt-3 rounded-2xl bg-diem-paper border border-diem-line p-4 flex items-center gap-3">
            <span className="w-2 h-2 rounded-full bg-diem-ok animate-pulse shrink-0" />
            <p className="text-sm text-diem-slate">Gate motors ordered · approved by Sarah · delivered Friday</p>
          </div>
        </Tile>

        <Tile icon={ListTodo} title="Tasks" sub="Things that need doing." className="lg:col-span-4" delay={0.05}>
          <div className="mt-5 space-y-2">
            {["Order gate motors", "Book electrician"].map((t, i) => (
              <div key={t} className="flex items-center gap-2.5 text-sm text-diem-slate">
                <span className={`w-4.5 h-4.5 w-[18px] h-[18px] rounded-md border-2 flex items-center justify-center ${i === 0 ? "border-diem-blue bg-diem-blue" : "border-diem-line"}`}>
                  {i === 0 && <Check className="w-3 h-3 text-white" />}
                </span>
                <span className={i === 0 ? "line-through opacity-50" : "font-medium"}>{t}</span>
              </div>
            ))}
          </div>
        </Tile>

        <Tile icon={Image} title="Photos" sub="Every project image together." className="lg:col-span-4" delay={0.1}>
          <div className="mt-5 grid grid-cols-3 gap-2">
            <img className="col-span-2 h-20 w-full object-cover rounded-xl" alt="Project build" loading="lazy"
              src="https://images.unsplash.com/photo-1753893558279-8beaad18dd96?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTF8MHwxfHNlYXJjaHw0fHxtb2Rlcm4lMjBob21lJTIwZXh0ZW5zaW9uJTIwYXJjaGl0ZWN0dXJlfGVufDB8fHx8MTc4NzgxOTE5Nnww&ixlib=rb-4.1.0&q=85&w=500" />
            <img className="h-20 w-full object-cover rounded-xl" alt="Finished room" loading="lazy"
              src="https://images.unsplash.com/photo-1628744876497-eb30460be9f6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzN8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBsaXZpbmclMjByb29tJTIwcmVub3ZhdGlvbnxlbnwwfHx8fDE3ODc4MTkxOTV8MA&ixlib=rb-4.1.0&q=85&w=300" />
          </div>
        </Tile>

        <Tile icon={CalendarClock} title="Deadlines" sub="Know what's coming next." className="lg:col-span-4" delay={0.05} />
        <Tile icon={BadgeCheck} title="Approvals" sub="Decisions without the confusion." className="lg:col-span-4" delay={0.1} />
        <Tile icon={Ruler} title="Measurements" sub="Important dimensions where you need them." className="lg:col-span-4" delay={0.15}>
          <p className="mt-4 font-display text-2xl font-semibold tracking-tight text-diem-ink">4200<span className="text-diem-blue text-base font-bold ml-1">mm</span></p>
          <p className="text-[11px] text-diem-muted font-medium">Driveway clear opening</p>
        </Tile>
        <Tile icon={FileText} title="Documents" sub="Files without endless scrolling." className="lg:col-span-4" delay={0.05} />
        <Tile icon={MapPin} title="Addresses" sub="Locations automatically captured." className="lg:col-span-4" delay={0.1} />
        <Tile icon={Users} title="Contacts" sub="Important people and numbers." className="lg:col-span-4" delay={0.15} />
        <Tile icon={History} title="Timeline" sub="See how the project developed." className="lg:col-span-8" delay={0.1}>
          <div className="mt-5 flex items-center gap-0">
            {["First message", "Measurements", "Approved", "Motors ordered"].map((s, i) => (
              <div key={s} className="flex items-center flex-1 last:flex-none">
                <div className="flex flex-col items-center gap-1.5">
                  <span className={`w-2.5 h-2.5 rounded-full ${i === 3 ? "bg-diem-blue ring-4 ring-diem-blue/15" : "bg-diem-line"}`} />
                  <span className="text-[10px] font-bold text-diem-muted whitespace-nowrap">{s}</span>
                </div>
                {i < 3 && <span className="flex-1 h-px bg-diem-line mx-2 mb-4" />}
              </div>
            ))}
          </div>
        </Tile>
      </div>
    </div>
  </section>
);
